import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '../atoms/Card';
import Eyebrow from '../atoms/Eyebrow';
import { intentGlyph, intentLabelKey } from '@/ui/utils/intent_glyph';
import type { SessionListItem } from '@/types/api';
import type { IntentVector } from '@/types/intent';

interface SessionListCardProps {
  // SessionListItem may carry an optional intent_vector (M0.5+); legacy
  // sessions and pre-M0.5 items omit it. The local intersection keeps
  // SessionListCard self-contained without touching types/api.ts.
  session: SessionListItem & { intent_vector?: IntentVector };
}

// PR-UX-08 organism · self-contained card for one session row.
// PR-UX-05 JTBD · primary link maps to user intent (resume live / view past report).
//
// FYD-2026-0512 dashboard-history-click-no-response: the whole card is now
// the click target so users can tap the question text (the obvious affordance)
// and not just the small inline action label at the bottom. Status →
// destination mapping lives in resolveDestination below.
export default function SessionListCard({ session: s }: SessionListCardProps) {
  const { t } = useTranslation();
  const v = s.intent_vector?.V;
  const dest = resolveDestination(s.status, s.session_id);

  const body = (
    <>
      <div className="flex items-baseline justify-between gap-4 mb-1">
        <p className="font-serif text-lg leading-snug truncate flex items-center gap-2">
          {v && (
            <span
              title={t(intentLabelKey(v))}
              aria-label={t(intentLabelKey(v))}
              className="font-mono text-xs text-ink-2 shrink-0"
              data-testid={`session-intent-glyph-${v}`}
            >
              {intentGlyph(v)}
            </span>
          )}
          <span className="truncate">{s.question}</span>
        </p>
        <Eyebrow tone={dest?.tone === 'live' ? 'azure' : 'ash'} className="shrink-0">
          {t(`dash.status.${s.status}`)}
        </Eyebrow>
      </div>
      <p className="text-sm text-ink-3 mb-3">
        {new Date(s.created_at).toLocaleDateString()}
        {s.picked_candidate ? ` · ${t('dash.pickedShort')} ${s.picked_candidate}` : ''}
      </p>
      {dest && (
        <div className="flex gap-4 text-sm">
          <span
            className={`${dest.tone === 'live' ? 'text-azure-700' : dest.tone === 'aborted' ? 'text-ink-3' : 'text-ink'} border-b border-current pb-px`}
            data-testid={dest.testid}
          >
            {t(dest.labelKey)}
          </span>
        </div>
      )}
    </>
  );

  // No destination (deleted_by_user / unknown future status): render the
  // card without a Link wrapper so users don't get a dead click.
  if (!dest) {
    return (
      <Card padding="normal" className="opacity-60" data-testid="session-list-card-no-action">
        {body}
      </Card>
    );
  }

  return (
    <Link
      to={dest.to}
      className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-azure-500 rounded-lg"
      data-testid="session-list-card-link"
    >
      <Card padding="normal" hover>
        {body}
      </Card>
    </Link>
  );
}

type Destination = {
  to: string;
  labelKey: string;
  tone: 'report' | 'live' | 'aborted';
  testid: string;
};

// resolveDestination — single source of truth for status → click destination.
// Extending status enums (e.g. 'paused') means adding a case here · falling
// through to null is a deliberate signal that the FE doesn't know what to do.
function resolveDestination(status: string, sessionId: string): Destination | null {
  switch (status) {
    case 'starting':
    case 'running':
      return {
        to: `/sessions/${sessionId}/live`,
        labelKey: 'dash.viewLive',
        tone: 'live',
        testid: 'session-list-card-live-label',
      };
    case 'resolved':
    case 'completed':
      return {
        to: `/sessions/${sessionId}/report`,
        labelKey: 'dash.viewReport',
        tone: 'report',
        testid: 'session-list-card-report-label',
      };
    case 'aborted':
      return {
        to: `/sessions/${sessionId}/report`,
        labelKey: 'dash.viewAborted',
        tone: 'aborted',
        testid: 'session-list-card-aborted-label',
      };
    default:
      return null;
  }
}
